import { z } from 'zod';
import { createToolSignature } from './tool-signature';

export const searchLinkedinNetworkRegexToolSignature = createToolSignature({
    name: 'searchLinkedinNetworkRegexTool',
    description: `
    Search the user's Linkedin network for people with regex. Can search by full name, location, current position, and all positions (both title and company name and Linkedin headline).
    Paginate the results by skipping the first specified amount of pages (1 is the first page). E.g. if the user asks you for all contacts of some sort and you get 200/200 profiles, fetch more than one page to make sure find all results and appropriate.
`,
    inputSchema: z.object({
        fullNameRegex: z
            .string()
            .optional()
            .describe('A case-insensitive regex to filter the full name of the person. Leave undefined to match all.'),
        locationRegex: z
            .string()
            .optional()
            .describe(
                'A case-insensitive regex to filter the location of the person, will not affect the relevance and order of results, so the location should also be added to the search query to get the most relevant results in your first response. Keep empty if you do not want to filter by location. Example: "london". Keep in mind that not all profiles have a location and mostly covers country and/or city, best usage is to combine one for city and one for country and post process the results to get a complete list of the most relevant ones. Leave undefined to match all.',
            ),
        currentPositionRegex: z
            .string()
            .optional()
            .describe(
                'A case-insensitive regex to filter the current position of the person. This will search in current company name, job title and headline. Leave undefined to match all.',
            ),
        allPositions: z
            .string()
            .optional()
            .describe(
                'A case-insensitive regex to filter all position of the person. This will search in all historical and current positions of the person for company name, job title, as well as the current headline. Leave undefined to match all.',
            ),
        limit: z
            .number()
            .min(1)
            .max(200)
            .describe(
                'The maximum number of profiles to return (must be between 1 and 200). If unsure, 20 is a good default.',
            ),
        page: z.number().describe('The page number to return (always start with page 1).'),
    }),
    outputSchema: z.object({
        profiles: z.array(
            z.object({
                name: z.string().describe('The name of the profile'),
                memberId: z.string().describe('The memberId of the profile'),
                publicIdentifier: z.string().describe('The public identifier of the profile'),
                headline: z.string().optional().describe('The Linkedin headline of the profile'),
                topExperience: z
                    .string()
                    .optional()
                    .describe('The top experience of the profile (company name @ job title)'),
            }),
        ),
    }),
    toolCallUi: (input) =>
        `Searching your Linkedin network:\n` +
        `- Position: ${input.allPositions || input.currentPositionRegex || 'All'}\n` +
        `- Location: ${input.locationRegex || 'All'}\n` +
        `- Full Name: ${input.fullNameRegex || 'All'}` +
        `${input.page > 1 ? `\n- Page: ${input.page}` : ''}`,
});

/**
 * @deprecated
 */
export const searchLinkedinNetworkEmbeddingToolSignature = createToolSignature({
    name: 'searchLinkedinNetworkEmbeddingTool',
    description: `
    Search the user's Linkedin network for people with free text, sorted by relevance. Returns 50 profiles per page. Paginate the results by skipping the first specified amount of pages (1 is the first page). E.g. if the user asks you for all contacts of some sort you should fetch more than one page to make sure find all results and appropriate.
`,
    inputSchema: z.object({
        searchQuery: z.string(),
        locationRegex: z
            .string()
            .describe(
                'A case-insensitive regex to filter the location of the profiles, will not affect the relevance and order of results, so the location should also be added to the search query to get the most relevant results in your first response. Keep empty if you do not want to filter by location. Example: "london". Keep in mind that not all profiles have a location and mostly covers country and/or city, best usage is to combine one for city and one for country and post process the results to get a complete list of the most relevant ones.',
            ),
        page: z.number().describe('The page number to return (always start with page 1).'),
    }),
    outputSchema: z.object({
        profiles: z.array(
            z.object({
                name: z.string().describe('The name of the profile'),
                memberId: z.string().describe('The memberId of the profile'),
                publicIdentifier: z.string().describe('The public identifier of the profile'),
            }),
        ),
    }),
    toolCallUi: (input) => `Searching your Linkedin network: "${input.searchQuery || ''}"`,
});

export const LinkedinNetworkAnalysisToolSignature = createToolSignature({
    name: 'LinkedinNetworkAnalysisTool',
    description:
        "Intelligently analyze the user's entire LinkedIn network with natural language queries. Use this tool to e.g. (1) Discover high-value contacts matching certain criteria, (2) Analyze network composition or patterns, (3) Identify strategic connection opportunities, or (4) Get recommendations based on their professional network. Formulate clear, specific queries focusing on professional attributes, industry terms, or relationship goals. Do not use this tool when looking for a specific connections.",
    inputSchema: z.object({
        searchQuery: z.string().describe('The query you want to ask the linkedin network agent'),
    }),
    outputSchema: z.string().describe('The response from the linkedin network agent'),
    toolCallUi: (input) => `Analyzing your linkedin network:\n${input.searchQuery || ''}`,
});

export const getLinkedinNetworkProfilesSummaryToolSignature = createToolSignature({
    name: 'getLinkedinNetworkProfilesSummaryTool',
    description: "Get a summary of the user's Linkedin network profiles",
    inputSchema: z.object({
        // limit: z.number().optional().describe('The maximum number of results to return (default: 4000)'),
        // skip: z.number().optional().describe('The number of results to skip (default: 0)'),
    }),
    outputSchema: z.array(z.string()),
    toolCallUi: (input) => `Checking your Linkedin network`,
});
